import { useMemo } from 'react';

import { IfFeatureState_Core } from 'core';
import { RouteWithHeaderBackButton } from 'core/Hooks';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useMatch,
  useSearchParams,
} from 'react-router-dom';

import { AddCard } from '../../pages/Account/AddCard/AddCard';
import { TicketPage } from '../../pages/Tickets/TicketPage';
import { TicketsPage } from '../../pages/Tickets/TicketsPage';
import { EarningEntryPage } from '../../pages/Wallet/Earnings/EarningDetail/EarningDetailPage';
import { EarningsPage } from '../../pages/Account/Rewards/EarningsPage';
import { WalletEventsPage } from '../../pages/Wallet/Events/WalletEventsPage';
import { PurchaseDetailPage } from '../../pages/Wallet/Purchases/PurchaseDetailPage/PurchaseDetailPage';
import { PurchasesPage } from '../../pages/Wallet/Purchases/PurchasesPage';
import { WalletPage } from '../../pages/Wallet/Wallet/WalletPage';
import { AuthenticatedLayout } from '../Layouts/StandardLayout/AuthenticatedLayout';
import { WalletLayout } from '../Layouts/Wallet.layout';

const PurchasesRoutes = () => {
  return (
    <Routes>
      <Route
        path=":cartItemId"
        element={
          <RouteWithHeaderBackButton>
            <PurchaseDetailPage />
          </RouteWithHeaderBackButton>
        }
      />
      <Route index element={<PurchasesPage />} />
    </Routes>
  );
};

const EarningRoutes = () => {
  return (
    <Routes>
      <Route
        path=":entryId"
        element={
          <RouteWithHeaderBackButton>
            <EarningEntryPage />
          </RouteWithHeaderBackButton>
        }
      />
      <Route index element={<EarningsPage />} />
    </Routes>
  );
};

const RedirectLegacyWalletRoutes = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const walletTicketsRouteMatch = useMatch('/wallet/tickets');
  const walletTicketIdRouteMatch = useMatch('/wallet/tickets/:ticketId');

  const redirectPath = useMemo(() => {
    // Extract the path segment after '/wallet/'
    const pathSegment = location.pathname.split('/wallet/')[1] || '';
    const searchParamString = searchParams.toString();
    const withSearchParams = (path: string) =>
      searchParamString ? `${path}?${searchParamString}` : path;

    // Route mapping: /wallet/* -> new destination
    if (!pathSegment) {
      return withSearchParams('/account');
    }

    if (pathSegment === 'earnings') {
      return withSearchParams('/account/rewards');
    }

    if (pathSegment === 'card') {
      return withSearchParams('/account/card');
    }

    if (walletTicketsRouteMatch) {
      return withSearchParams('/tickets');
    }

    if (walletTicketIdRouteMatch) {
      return withSearchParams(`/tickets/${walletTicketIdRouteMatch.params.ticketId}`);
    }

    // Default: move everything else under /account
    return withSearchParams(`/account/${pathSegment}`);
  }, [location.pathname, searchParams, walletTicketsRouteMatch, walletTicketIdRouteMatch]);

  return <Navigate to={redirectPath} replace />;
};

const WalletRoutesFeatureFlaggedLayout = () => {
  return (
    <IfFeatureState_Core featureId="user.account_page" fallBack={<Outlet />}>
      <RedirectLegacyWalletRoutes />
    </IfFeatureState_Core>
  );
};

const WalletRoutes = () => {
  return (
    <Routes>
      <Route element={<WalletRoutesFeatureFlaggedLayout />}>
        <Route
          path="tickets"
          element={
            <RouteWithHeaderBackButton>
              <TicketsPage />
            </RouteWithHeaderBackButton>
          }
        />

        <Route path="tickets/:ticketId" element={<TicketPage />} />

        <Route element={<AuthenticatedLayout />}>
          <Route element={<WalletLayout />}>
            <Route path="card" element={<AddCard />} />
            <Route path="wallet" element={<WalletPage />} />
            <Route path="earnings" element={<EarningsPage />} />
            <Route path="events/:eventId" element={<WalletEventsPage />} />

            <Route path="purchases/*" element={<PurchasesRoutes />} />
            <Route path="earnings*" element={<EarningRoutes />} />
            <Route index element={<WalletPage />} />
            <Route path="*" element={<WalletPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
};

export { WalletRoutes };
